import { memo, useCallback, useMemo, type FC, type MouseEvent, type ReactNode } from "react";
import type { SelectProps } from "antd";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import StyledDropdown from "../StyledDropdown";
import StyledTooltip from "../StyledTooltip/StyledTooltip";
import TruncatedTooltipText from "../StyledTooltip/TruncatedTooltipText";
import type { AudienceListOption } from "src/utils/audienceListLabels";
import "./AudienceListSelect.scss";

const VISIBLE_TAG_COUNT = 1;

type TagRender = NonNullable<SelectProps["tagRender"]>;
type OptionRender = NonNullable<SelectProps["optionRender"]>;
type MaxTagPlaceholder = (_omittedValues: { label?: ReactNode }[]) => ReactNode;

export interface AudienceListSelectProps {
  id?: string;
  value: string[];
  options: AudienceListOption[];
  onChange: (_value: string[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  showOmittedTooltip?: boolean;
  showSelectedIcon?: boolean;
  getPopupContainer?: SelectProps["getPopupContainer"];
  className?: string;
  popupClassName?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

const getLabelText = (label: ReactNode) =>
  typeof label === "string" || typeof label === "number" ? String(label) : "";

const preventTagMouseDown = (event: MouseEvent<HTMLElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

const AudienceListSelect: FC<AudienceListSelectProps> = ({
  id,
  value,
  options,
  onChange,
  onBlur,
  placeholder,
  loading = false,
  disabled = false,
  showOmittedTooltip = true,
  showSelectedIcon = false,
  getPopupContainer,
  className = "",
  popupClassName = "",
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}) => {
  const { t, i18n } = useTranslation();

  const optionsByName = useMemo(
    () => new Map(options.map(option => [option.name, option])),
    [options]
  );

  const selectOptions = useMemo(
    () => options.map(option => ({ value: option.name, label: option.displayName })),
    [options]
  );

  const renderTag = useCallback<TagRender>(
    ({ label, closable, onClose }) => (
      <span className="audience-list-select__tag">
        <TruncatedTooltipText
          text={getLabelText(label)}
          className="audience-list-select__tag-label"
        >
          {label}
        </TruncatedTooltipText>
        {closable && (
          <button
            type="button"
            className="audience-list-select__tag-remove"
            aria-label={t("audienceListSelect.remove")}
            onMouseDown={preventTagMouseDown}
            onClick={onClose}
          >
            <X size={10} aria-hidden />
          </button>
        )}
      </span>
    ),
    [t]
  );

  const renderMaxTagPlaceholder = useCallback<MaxTagPlaceholder>(
    omittedValues => {
      const moreTag = (
        <span className="audience-list-select__tag audience-list-select__tag--more">
          +{omittedValues.length}
        </span>
      );

      if (!showOmittedTooltip) {
        return moreTag;
      }

      return (
        <StyledTooltip
          content={
            <span className="audience-list-select__more-tooltip">
              {omittedValues.map(({ label }, index) => (
                <span key={index}>{label}</span>
              ))}
            </span>
          }
        >
          {moreTag}
        </StyledTooltip>
      );
    },
    [showOmittedTooltip]
  );

  const renderOption = useCallback<OptionRender>(
    option => {
      const audience = optionsByName.get(String(option.value));
      if (!audience) {
        return option.label;
      }

      const { displayName, description, customerCount } = audience;

      return (
        <div className="audience-list-option">
          <div className="audience-list-option__header">
            <span className="audience-list-option__title">{displayName}</span>
            {customerCount !== undefined && (
              <span className="audience-list-option__count">
                {t("audienceListSelect.customersCount", {
                  count: customerCount,
                  formattedCount: customerCount.toLocaleString(i18n.language),
                })}
              </span>
            )}
          </div>
          {description && <p className="audience-list-option__description">{description}</p>}
        </div>
      );
    },
    [optionsByName, t, i18n.language]
  );

  return (
    <StyledDropdown
      id={id}
      mode="multiple"
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      options={selectOptions}
      placeholder={placeholder}
      loading={loading}
      disabled={disabled}
      showSearch
      optionFilterProp="label"
      tagRender={renderTag}
      maxTagCount={VISIBLE_TAG_COUNT}
      maxTagPlaceholder={renderMaxTagPlaceholder}
      optionRender={renderOption}
      menuItemSelectedIcon={showSelectedIcon ? undefined : null}
      getPopupContainer={getPopupContainer}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      className={`audience-list-select ${className}`}
      popupClassName={`audience-list-select__popup ${popupClassName}`}
    />
  );
};

export default memo(AudienceListSelect);
